import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { Box, FormControl, InputLabel } from "@mui/material";
import { DailyTime, DropListItem } from "../../../models";
import {
  joinDelimitedValues,
  parseDelimitedValues,
} from "../../../utils/commonFunctions";

interface Props {
  list: DropListItem[];
  fieldKey: keyof DailyTime;
  setWeekData?: (update: (weekData: DailyTime[]) => DailyTime[]) => void;
  onChange?: (value: string[]) => void;
  rowIndex: number;
  value?: string[];
  enablePortal?: boolean;
  IconComponent?: React.ElementType;
  sx?: object;
  inputBackgroundColor?: string;
  label?: string;
  placeholder?: string;
}

const MultiSelectInput: React.FC<Props> = ({
  list,
  fieldKey,
  setWeekData,
  onChange,
  rowIndex,
  value = [],
  enablePortal = false,
  IconComponent = () => null,
  sx = {},
  inputBackgroundColor = "transparent",
  label,
  placeholder = "Select",
}) => {
  const MIN_TRUNCATE_WIDTH = 120;
  const SELECT_FONT = "400 14px Roboto, Helvetica, Arial, sans-serif";
  const MAX_TRUNCATE_CACHE_SIZE = 300;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const measureContextRef = useRef<CanvasRenderingContext2D | null>(null);
  const truncateCacheRef = useRef<Map<string, string>>(new Map());
  const [containerWidth, setContainerWidth] = useState(0);
  const SELECT_ALL_VALUE = "__select_all__";

  const options = useMemo(
    () =>
      list.filter(
        (item): item is DropListItem & { value: string } => !!item.value,
      ),
    [list],
  );
  const optionValues = useMemo(
    () => options.map((item) => item.value),
    [options],
  );

  const selectedValues = useMemo(() => {
    return Array.isArray(value) ? value.filter(Boolean) : [];
  }, [value]);

  const hasSelected = selectedValues.length > 0;
  const selectedCount = selectedValues.filter((v) =>
    optionValues.includes(v),
  ).length;
  const allSelected =
    optionValues.length > 0 && selectedCount === optionValues.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const applySelectedValues = (selected: string[]) => {
    setWeekData?.((prevWeekData: DailyTime[]) => {
      const newWeekData = [...prevWeekData];
      if (rowIndex >= 0 && rowIndex < newWeekData.length) {
        newWeekData[rowIndex] = {
          ...newWeekData[rowIndex],
          [fieldKey]: joinDelimitedValues(selected),
        };
      }
      return newWeekData;
    });

    onChange?.(selected);
  };

  useEffect(() => {
    if (list.length === 1 && !hasSelected) {
      applySelectedValues(list[0].value ? [list[0].value] : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, hasSelected, setWeekData, rowIndex, fieldKey, onChange]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const selectedRaw =
      typeof event.target.value === "string"
        ? parseDelimitedValues(event.target.value)
        : event.target.value;

    if (selectedRaw.includes(SELECT_ALL_VALUE)) {
      applySelectedValues(allSelected ? [] : optionValues);
      return;
    }

    const selected = selectedRaw.filter((v) => optionValues.includes(v));

    applySelectedValues(selected);
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      setContainerWidth(containerRef.current?.clientWidth || 0);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  const truncateByWidth = useCallback((text: string, maxWidth: number) => {
    if (!text || maxWidth <= 0) return text;

    const cacheKey = `${maxWidth}::${text}`;
    const cachedResult = truncateCacheRef.current.get(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }

    if (!measureContextRef.current) {
      if (!measureCanvasRef.current) {
        measureCanvasRef.current = document.createElement("canvas");
      }

      measureContextRef.current = measureCanvasRef.current.getContext("2d");
    }

    const context = measureContextRef.current;
    if (!context) return text;

    // Match MUI Select font defaults used in this app.
    context.font = SELECT_FONT;

    if (context.measureText(text).width <= maxWidth) return text;

    const ellipsis = "...";
    let left = 0;
    let right = text.length;
    let result = ellipsis;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const candidate = `${text.slice(0, mid)}${ellipsis}`;

      if (context.measureText(candidate).width <= maxWidth) {
        result = candidate;
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    if (truncateCacheRef.current.size >= MAX_TRUNCATE_CACHE_SIZE) {
      truncateCacheRef.current.clear();
    }
    truncateCacheRef.current.set(cacheKey, result);

    return result;
  }, []);

  return (
    <Box sx={{ width: "100%" }} ref={containerRef}>
      {enablePortal ? (
        <FormControl variant="outlined" fullWidth size="small">
          {label && (
            <InputLabel shrink id={`multi-select-label-${fieldKey}`}>
              {label}
            </InputLabel>
          )}
          <Select
            labelId={label ? `multi-select-label-${fieldKey}` : undefined}
            label={label}
            multiple
            value={selectedValues}
            onChange={handleChange}
            renderValue={(selected) => {
              const selectedArray = selected as string[];
              if (selectedArray.length === 0)
                return (
                  <span
                    style={{
                      backgroundColor: inputBackgroundColor,
                      display: "inline-block",
                      width: "100%",
                    }}
                  >
                    {placeholder}
                  </span>
                );
              const joined = selectedArray.join("; ");
              // Approximate available width after paddings and icon.
              const maxTextWidth = Math.max(
                containerWidth - 64,
                MIN_TRUNCATE_WIDTH,
              );
              const displayValue = truncateByWidth(joined, maxTextWidth);
              return (
                <span
                  style={{
                    backgroundColor: inputBackgroundColor,
                    display: "block",
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={joined}
                >
                  {displayValue}
                </span>
              );
            }}
            displayEmpty
            fullWidth
            size="small"
            IconComponent={IconComponent}
            sx={sx}
            disabled={options.length < 2}
          >
            {options.length > 1 && (
              <MenuItem value={SELECT_ALL_VALUE}>
                <Checkbox checked={allSelected} indeterminate={someSelected} />
                <ListItemText
                  primary={`${allSelected ? "Unselect" : "Select"} all ${fieldKey.toLocaleLowerCase()}`}
                />
              </MenuItem>
            )}
            {options.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                <Checkbox checked={selectedValues.indexOf(item.value) > -1} />
                <ListItemText primary={item.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
    </Box>
  );
};

export default MultiSelectInput;
